
import React from 'react';

interface RebateBreakdownProps {
  solarOnly: boolean;
  solarPlusAC: boolean;
  solarACInsulation: boolean;
  solarCost: number;
  acCost: number;
  insulationCost: number;
}

export default function RebateBreakdown({
  solarOnly,
  solarPlusAC,
  solarACInsulation,
  solarCost,
  acCost,
  insulationCost,
}: RebateBreakdownProps) {
  const safeNumber = (value: any): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const _solarCost = safeNumber(solarCost);
  const _acCost = safeNumber(acCost);
  const _insulationCost = safeNumber(insulationCost);

  const hasAny = solarOnly || solarPlusAC || solarACInsulation;
  if (!hasAny) return null;

  const solarITC = Math.round(_solarCost * 0.3);
  const acTaxCredit = 2000;
  const insulationTaxCredit = Math.min(Math.round(_insulationCost * 0.3), 1200);
  const utilityRebateAC = 750;
  const utilityRebateInsulation = 600;
  const salesTaxSaved = Math.round(_solarCost * 0.06);

  const fmt = (value: number) => `$${value.toLocaleString()}`;

  const calculateTotal = () => {
    let total = 0;
    if (solarOnly || solarPlusAC || solarACInsulation) {
      total += solarITC + salesTaxSaved;
    }
    if (solarPlusAC || solarACInsulation) {
      total += acTaxCredit + utilityRebateAC;
    }
    if (solarACInsulation) {
      total += insulationTaxCredit + utilityRebateInsulation;
    }
    return total;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-navy-light">
          <i className="ri-government-line text-brand-navy text-xl"></i>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">
            Tax Credits &amp; Rebates You May Qualify For
          </h3>
          <p className="text-xs text-gray-500">
            Based on your selected upgrades - 2025 rates
          </p>
        </div>
      </div>

      {/* Federal Solar ITC */}
      {(solarOnly || solarPlusAC || solarACInsulation) && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-green-100 flex-shrink-0">
              <i className="ri-sun-line text-green-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Federal Solar Tax Credit (ITC)
                </h4>
                <span className="text-lg font-bold text-green-600">
                  {fmt(solarITC)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                30% of your total solar system cost is credited directly against
                your federal income tax. This is a dollar-for-dollar reduction in
                taxes owed.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] bg-brand-red-light text-brand-red px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                  <i className="ri-alarm-warning-line"></i> DEADLINE: Dec 31,
                  2025
                </span>
                <span className="text-[10px] text-gray-500">
                  for homeowner-owned systems
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FL Sales Tax Exemption */}
      {(solarOnly || solarPlusAC || solarACInsulation) && (
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-navy-light flex-shrink-0">
              <i className="ri-percent-line text-brand-navy text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Florida Sales Tax Exemption
                </h4>
                <span className="text-lg font-bold text-green-600">
                  {fmt(salesTaxSaved)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Florida provides a 100% exemption from the 6% state sales tax on
                all solar energy equipment. This saves you thousands
                automatically at purchase.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FL Property Tax Exemption */}
      {(solarOnly || solarPlusAC || solarACInsulation) && (
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-navy-light flex-shrink-0">
              <i className="ri-home-heart-line text-brand-navy text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Florida Property Tax Exemption
                </h4>
                <span className="text-lg font-bold text-green-600">100%</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                The added home value from your solar installation is 100%
                exempt from property taxes. Your home value increases without
                higher property taxes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Net Metering */}
      {(solarOnly || solarPlusAC || solarACInsulation) && (
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-navy-light flex-shrink-0">
              <i className="ri-exchange-line text-brand-navy text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Florida Net Metering (1:1)
                </h4>
                <span className="text-lg font-bold text-green-600">
                  Full Credit
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Excess solar energy sent back to the grid earns you full retail
                credit on your bill. FPL, Duke Energy, and TECO all participate.
                Your meter literally runs backward.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HVAC Federal Tax Credit */}
      {(solarPlusAC || solarACInsulation) && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-green-100 flex-shrink-0">
              <i className="ri-temp-cold-line text-green-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  HVAC Federal Tax Credit (25C)
                </h4>
                <span className="text-lg font-bold text-green-600">
                  {fmt(acTaxCredit)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                30% of the cost of a qualifying heat pump or high-efficiency AC
                (17+ SEER2), capped at $2,000. Includes labor costs. Filed with
                IRS Form 5695.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HVAC Utility Rebate */}
      {(solarPlusAC || solarACInsulation) && (
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold-light flex-shrink-0">
              <i className="ri-flashlight-line text-brand-gold text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Utility HVAC Rebate
                </h4>
                <span className="text-lg font-bold text-green-600">
                  Up to {fmt(utilityRebateAC)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                FPL offers $150-$750, Duke Energy $300-$1,000, TECO $40-$550.
                Requires a Home Energy Check and use of an approved contractor.
                Stackable with federal credits.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Insulation Federal Tax Credit */}
      {solarACInsulation && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-green-100 flex-shrink-0">
              <i className="ri-home-4-line text-green-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Insulation Federal Tax Credit (25C)
                </h4>
                <span className="text-lg font-bold text-green-600">
                  {fmt(insulationTaxCredit)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                30% of insulation material costs, capped at $1,200/year. R-38
                attic insulation qualifies. Materials only (labor not covered).
                Filed with IRS Form 5695.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Insulation Utility Rebate */}
      {solarACInsulation && (
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold-light flex-shrink-0">
              <i className="ri-flashlight-line text-brand-gold text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                  Utility Insulation Rebate
                </h4>
                <span className="text-lg font-bold text-green-600">
                  Up to {fmt(utilityRebateInsulation)}
                </span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Duke Energy offers up to $800, FPL $220, TECO $0.16/sq ft, JEA
                $200. Based on current insulation level and square footage.
                Requires energy audit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Total Incentives Summary */}
      <div className="bg-gradient-to-r from-brand-navy to-brand-navy-dark rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-sm">Total Potential Incentives</h4>
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20">
            <i className="ri-gift-line text-white"></i>
          </div>
        </div>
        <div className="text-4xl font-bold mb-1">
          {fmt(calculateTotal())}
        </div>
        <p className="text-xs text-white/70">
          in tax credits, rebates, and exemptions
        </p>
        <p className="text-[10px] text-white/50 mt-3">
          * Actual amounts depend on your utility provider, tax situation, and
          system specifications. Consult a tax professional.
        </p>
      </div>
    </div>
  );
}
